/**
 * Thin client for the public Wikipedia APIs. Everything runs in the browser:
 * the Action API is called with origin=* (anonymous CORS) and the REST API is CORS-enabled.
 */

const USER_AGENT = 'RabbitHoleGame/0.1 (https://github.com/brennengreen/rabbit-hole)';

let lang = 'en';
export const setLang = (l: string): void => {
  lang = /^[a-z-]{2,12}$/.test(l) ? l : 'en';
};
export const getLang = (): string => lang;

export class WikiError extends Error {
  constructor(message: string, readonly kind: 'missing' | 'network' | 'api') {
    super(message);
  }
}

export interface ParsedPage {
  title: string;
  pageId: number;
  revId: number;
  html: string;
  displayTitle: string;
  description: string;
}

export interface Summary {
  title: string;
  description?: string;
  extract?: string;
  thumbnail?: { source: string; width: number; height: number };
}

/** MediaWiki-style title normalization (underscores, whitespace, first-letter case). */
export function normalizeTitle(t: string): string {
  const s = t.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function articleUrl(title: string, l = lang): string {
  return `https://${l}.wikipedia.org/wiki/${encodeURIComponent(title.replace(/ /g, '_'))}`;
}

function actionUrl(params: Record<string, string | number>): string {
  const p = new URLSearchParams({ format: 'json', formatversion: '2', origin: '*' });
  for (const [k, v] of Object.entries(params)) p.set(k, String(v));
  return `https://${lang}.wikipedia.org/w/api.php?${p}`;
}

async function getJSON(url: string, signal?: AbortSignal): Promise<any> {
  let res: Response;
  try {
    res = await fetch(url, { headers: { 'Api-User-Agent': USER_AGENT }, signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new WikiError('Could not reach Wikipedia. Check your connection.', 'network');
  }
  if (res.status === 404) throw new WikiError('Article not found.', 'missing');
  if (!res.ok) throw new WikiError(`Wikipedia returned HTTP ${res.status}.`, 'network');
  const j = await res.json();
  if (j && j.error) {
    const code = String(j.error.code ?? '');
    throw new WikiError(j.error.info ?? code, code === 'missingtitle' || code === 'invalidtitle' ? 'missing' : 'api');
  }
  return j;
}

/** Tiny bounded cache of in-flight/settled promises; failures are evicted so they can be retried. */
class PromiseCache<T> {
  private map = new Map<string, Promise<T>>();
  constructor(private max: number) {}
  get(key: string, make: () => Promise<T>): Promise<T> {
    const hit = this.map.get(key);
    if (hit) {
      this.map.delete(key);
      this.map.set(key, hit);
      return hit;
    }
    const p = make();
    this.map.set(key, p);
    p.catch(() => this.map.delete(key));
    while (this.map.size > this.max) this.map.delete(this.map.keys().next().value as string);
    return p;
  }
}

const pageCache = new PromiseCache<ParsedPage>(16);
const summaryCache = new PromiseCache<Summary>(200);

function htmlToText(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  return (doc.body.textContent ?? '').trim();
}

export function fetchPage(title: string): Promise<ParsedPage> {
  const key = `${lang}:${normalizeTitle(title)}`;
  return pageCache.get(key, async () => {
    const j = await getJSON(
      actionUrl({
        action: 'parse',
        page: title,
        redirects: 1,
        prop: 'text|displaytitle|revid|properties',
        disableeditsection: 1,
        disablelimitreport: 1,
      }),
    );
    const p = j.parse;
    const props = p.properties ?? {};
    const description: string = Array.isArray(props)
      ? (props.find((x: any) => x.name === 'wikibase-shortdesc')?.['*'] ?? '')
      : (props['wikibase-shortdesc'] ?? '');
    return {
      title: p.title,
      pageId: p.pageid,
      revId: p.revid,
      html: p.text ?? '',
      displayTitle: htmlToText(p.displaytitle) || p.title,
      description,
    };
  });
}

export function fetchSummary(title: string): Promise<Summary> {
  const key = `${lang}:${normalizeTitle(title)}`;
  return summaryCache.get(key, async () => {
    const url = `https://${lang}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title.replace(/ /g, '_'))}`;
    const j = await getJSON(url);
    return {
      title: j.title ?? title,
      description: j.description,
      extract: j.extract,
      thumbnail: j.thumbnail,
    };
  });
}

export async function searchTitles(q: string, signal?: AbortSignal): Promise<string[]> {
  if (!q.trim()) return [];
  const j = await getJSON(actionUrl({ action: 'opensearch', search: q, namespace: 0, limit: 8 }), signal);
  return Array.isArray(j?.[1]) ? j[1] : [];
}

export async function randomTitle(): Promise<string> {
  const j = await getJSON(actionUrl({ action: 'query', list: 'random', rnnamespace: 0, rnlimit: 1 }));
  return j.query.random[0].title;
}

/** Canonicalize a user-entered title (follows redirects). Returns null when the page is missing. */
export async function resolveTitle(title: string): Promise<string | null> {
  const j = await getJSON(actionUrl({ action: 'query', titles: title, redirects: 1 }));
  const page = j.query?.pages?.[0];
  if (!page || page.missing || page.invalid) return null;
  return page.title;
}

const redirectCache = new Map<string, string>();

/** Resolve redirect titles to their targets, 50 at a time. Unknown/non-redirect titles map to themselves. */
export async function resolveRedirects(titles: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const todo: string[] = [];
  for (const t of titles) {
    const hit = redirectCache.get(`${lang}:${t}`);
    if (hit) out.set(t, hit);
    else todo.push(t);
  }
  for (let i = 0; i < todo.length; i += 50) {
    const batch = todo.slice(i, i + 50);
    try {
      const j = await getJSON(actionUrl({ action: 'query', titles: batch.join('|'), redirects: 1 }));
      const norm = new Map<string, string>();
      for (const n of j.query?.normalized ?? []) norm.set(n.from, n.to);
      const red = new Map<string, string>();
      for (const r of j.query?.redirects ?? []) red.set(r.from, r.to);
      for (const t of batch) {
        const n = norm.get(t) ?? t;
        const final = red.get(n) ?? n;
        redirectCache.set(`${lang}:${t}`, final);
        out.set(t, final);
      }
    } catch {
      for (const t of batch) out.set(t, t);
    }
  }
  return out;
}

export interface TargetGraph {
  /** Canonical title plus every redirect that points at it. */
  aliases: Set<string>;
  /** Pages that link to the target (sampled; capped). */
  backlinks: Set<string>;
  /** Pages the target links to (capped). */
  forward: Set<string>;
}

async function collect(
  params: Record<string, string | number>,
  pick: (j: any, add: (t: string) => void) => void,
  maxRequests: number,
): Promise<Set<string>> {
  const set = new Set<string>();
  let cont: Record<string, string> = {};
  for (let i = 0; i < maxRequests; i++) {
    let j: any;
    try {
      j = await getJSON(actionUrl({ ...params, ...cont }));
    } catch (e) {
      // No data at all is an error the caller can retry; a failed continuation still leaves useful partial data.
      if (i === 0) throw e;
      break;
    }
    pick(j, (t) => set.add(t));
    if (!j.continue) break;
    cont = {};
    for (const [k, v] of Object.entries(j.continue)) cont[k] = String(v);
  }
  return set;
}

export async function fetchTargetGraph(title: string): Promise<TargetGraph> {
  const [aliases, backlinks, forward] = await Promise.all([
    collect(
      { action: 'query', titles: title, prop: 'redirects', rdlimit: 'max', rdnamespace: 0 },
      (j, add) => {
        for (const p of j.query?.pages ?? []) {
          add(p.title);
          for (const r of p.redirects ?? []) add(r.title);
        }
      },
      3,
    ),
    collect(
      { action: 'query', list: 'backlinks', bltitle: title, blnamespace: 0, bllimit: 'max', blredirect: 1 },
      (j, add) => {
        for (const b of j.query?.backlinks ?? []) {
          if (!b.redirect) add(b.title);
          for (const r of b.redirlinks ?? []) add(r.title);
        }
      },
      4,
    ),
    collect(
      { action: 'query', titles: title, prop: 'links', plnamespace: 0, pllimit: 'max' },
      (j, add) => {
        for (const p of j.query?.pages ?? []) for (const l of p.links ?? []) add(l.title);
      },
      3,
    ),
  ]);
  aliases.add(normalizeTitle(title));
  return { aliases, backlinks, forward };
}
